import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as dashboardApi from '../api/dashboard';
import type { DashboardData } from '../api/dashboard';
import type { DashboardToday } from '@peles/shared';

export function useDashboardStats() {
  return useQuery<DashboardData>({
    queryKey: ['dashboard', 'stats'],
    // A fresh window every fetch, so a dashboard left open overnight moves on.
    queryFn: () => dashboardApi.fetchDashboardStats(),
    staleTime: 60_000, // 1 minute
    refetchInterval: 5 * 60_000, // Auto-refresh every 5 minutes
  });
}

/** Whether two days are the same local day: same date and same window. */
export function sameLocalDay(a: dashboardApi.LocalDay, b: dashboardApi.LocalDay): boolean {
  return a.date === b.date && a.start === b.start && a.end === b.end && a.weekEnd === b.weekEnd;
}

/**
 * The browser's current day, moving on at local midnight — so a dashboard
 * left open overnight asks for the new day rather than refreshing the old one
 * every five minutes. A timer can oversleep with the laptop, so returning to
 * the tab checks too. The date and the whole window are compared: a laptop
 * that changes time zone can keep its date but not its day's boundaries, and
 * one that crosses the Date Line can keep the boundaries but not the date.
 * Every check re-arms the timer, so one that fires early (after a time-zone
 * change) never leaves the day without another.
 */
export function useLocalDay(): dashboardApi.LocalDay {
  const [day, setDay] = useState(() => dashboardApi.localDay());
  const [checks, setChecks] = useState(0);

  useEffect(() => {
    const roll = () => {
      setDay((current) => {
        const next = dashboardApi.localDay();
        return sameLocalDay(next, current) ? current : next;
      });
      setChecks((n) => n + 1);
    };
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const timer = setTimeout(roll, midnight.getTime() - now.getTime() + 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') roll();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checks]);

  return day;
}

/** The dashboard's "Today" band, for the browser's own day. */
export function useDashboardToday() {
  const day = useLocalDay();
  return useQuery<DashboardToday>({
    // Date and window both: the same date in another time zone is another day,
    // and across the Date Line the same window can fall on another date.
    queryKey: ['dashboard', 'today', day.date, day.start, day.weekEnd],
    queryFn: () => dashboardApi.fetchDashboardToday(day),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}
